import { createSelector } from '@reduxjs/toolkit';

import products from 'domains/products';
import optionGroups from 'domains/option_groups';
import activeEditor from 'domains/active_editor';
import canvases from 'domains/canvases';

import { byId } from 'utils/common';

import type { CanvasConfiguration } from 'domains/canvases';
import type { OptionGroupAPIData } from 'domains/option_groups';
import type { Dictionary } from 'types/common_types';
import type { ProductTemplateAPIData } from 'types/catalog_types';

export function getCanvasProductPrice(
  canvas: CanvasConfiguration,
  productsById: Dictionary<ProductTemplateAPIData>
) {
  const { product: productId, customPrice } = canvas;

  if (!productId) return 0;

  const product = productsById[productId];
  const productPrice = product && product.price;
  // Return 0 by default if product data is not available yet
  return parseFloat(customPrice || productPrice || '0');
}

export const selectProductPrice = createSelector(
  [activeEditor.selectors.canvas, products.selectors.selectEntities],
  (activeCanvas, productsById) => {
    if (!activeCanvas) return 0;

    return getCanvasProductPrice(activeCanvas, productsById);
  }
);

export function getCanvasOptionPrices(
  canvas: CanvasConfiguration,
  optionGroupsById: Dictionary<OptionGroupAPIData>
) {
  const { options, customOptionPrices } = canvas;
  const optionPrices: Record<string, number> = {};

  if (!options) return {};

  Object.entries(options).forEach(([optionGroupId, optionId]) => {
    const option = optionGroupsById[optionGroupId]?.options.find(byId(optionId));

    if (customOptionPrices && customOptionPrices[optionGroupId]) {
      optionPrices[optionGroupId] = parseFloat(customOptionPrices[optionGroupId]);
    } else if (option && !option.hidden) {
      optionPrices[optionGroupId] = parseFloat(option.service.price);
    } else {
      // Return 0 by default, if we don't have the data available yet
      optionPrices[optionGroupId] = 0;
    }
  });

  return optionPrices;
}

export const selectOptionPrices = createSelector(
  [activeEditor.selectors.canvas, optionGroups.selectors.selectEntities],
  (activeCanvas, optionGroupsById) => {
    if (!activeCanvas) return {};
    return getCanvasOptionPrices(activeCanvas, optionGroupsById);
  }
);

export const selectPriceTotal = createSelector(
  [
    canvases.selectors.selectAll,
    optionGroups.selectors.selectEntities,
    products.selectors.selectEntities,
  ],
  (allCanvases, optionGroupsById, productsById) => {
    const productsTotal = allCanvases.reduce((total, canvas) => {
      return total + getCanvasProductPrice(canvas, productsById);
    }, 0);

    const optionsTotal = allCanvases.reduce((total, canvas) => {
      const options = Object.values(getCanvasOptionPrices(canvas, optionGroupsById));
      return total + options.reduce((canvasOptionTotal, price) => price + canvasOptionTotal, 0);
    }, 0);

    return productsTotal + optionsTotal;
  }
);
